/**
 * Package Size Calculator for Shopify Cart
 * Determines package size (S/M/L) from max variant custom.length; sets cart attributes.
 * Only shipping_category (CAT_S/CAT_M/CAT_L) is used by Puco/checkout. Other attributes are for personal use.
 *
 * Version history:
 *   3.2  CAT_ prefix on shipping_category for checkout rule "does not contain CAT_"
 *   3.3  Puco "show only" per category; MAX_LENGTH_CM_FOR_PUCO config; debug __pkgCalcLastAttributes
 *   3.4  Puco matches only shipping_category; max_length_cm/package_label/package_size for personal use only
 *   3.5  Clear attributes on empty cart; API version config; replace eval; warn on missing length data
 */

(function() {
  'use strict';

  // ========== CONFIGURATION ==========

  /** Prefix for shipping_category. Checkout rule "does not contain CAT_" then matches only when attribute is missing (e.g. Apple Pay). */
  const SHIPPING_CATEGORY_PREFIX = 'CAT_';
  /** Only these values are allowed for shipping_category. Nothing else may be set. */
  const ALLOWED_SIZES = ['S', 'M', 'L'];

  const STOREFRONT_API_VERSION = '2026-01';

  const DEBUG = true;

  /** Set enabled: true and value to e.g. 'CAT_S', 'CAT_M', 'CAT_L' to force shipping_category and skip calculation. */
  const DEBUG_OVERRIDE_CATEGORY = { enabled: false, value: 'DEBUG_VALUE' };

  // State management
  let isCalculating = false;
  let lastCategory = null;
  let isUpdating = false;

  // Metafield cache: populated by Liquid script (cart:data:updated) and Storefront API
  // Key: variant_id, Value: { length: number }
  const metafieldCache = {};

  // ========== HELPER FUNCTIONS ==========

  function log(...args) {
    if (DEBUG) console.log('[PkgCalc]', ...args);
  }

  // ========== CORE FUNCTIONS ==========

  /**
   * Get the length (in cm) for a variant from the metafield cache.
   */
  function getVariantLength(variantId, productTitle, variantTitle) {
    const cached = metafieldCache[variantId];
    if (cached && cached.length > 0) {
      log(`[${variantId}] custom.length: ${cached.length}cm`);
      return cached.length;
    }

    log(`[${variantId}] "${productTitle}" / "${variantTitle}" — custom.length not set`);
    return 0;
  }

  /**
   * Ingest metafield data from the Liquid-rendered window.cartData.
   * Called whenever cart:data:updated fires (after Liquid section re-render).
   */
  function ingestLiquidMetafields() {
    if (!window.cartData) return;

    for (const item of window.cartData) {
      const val = parseFloat(
        item.variant_metafields?.custom?.length ??
        item.variant?.metafields?.custom?.length ??
        item.metafields?.custom?.length ??
        0
      );
      if (!isNaN(val) && val > 0) {
        metafieldCache[item.variant_id] = { length: val };
      }
    }

    log('Ingested Liquid metafields, cache:', { ...metafieldCache });
  }

  /**
   * Fetch variant metafields from Shopify Storefront API and store in cache.
   */
  async function fetchAndCacheVariantMetafield(variantId) {
    const query = `
      query getVariantMetafields($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            id
            lengthMetafield: metafield(namespace: "custom", key: "length") {
              value
              type
            }
          }
        }
      }
    `;

    const gid = `gid://shopify/ProductVariant/${variantId}`;

    try {
      const response = await fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.Shopify?.storefrontAccessToken || ''
        },
        body: JSON.stringify({ query, variables: { id: gid } })
      });

      if (!response.ok) throw new Error(`Storefront API HTTP ${response.status}`);

      const result = await response.json();
      if (result.errors) {
        console.error('[PkgCalc] Storefront API errors:', result.errors);
        return;
      }

      const lengthMetafield = result.data?.node?.lengthMetafield;
      if (lengthMetafield && lengthMetafield.value) {
        const val = parseFloat(lengthMetafield.value);
        if (!isNaN(val) && val > 0) {
          metafieldCache[variantId] = { length: val };
          log(`[${variantId}] API metafield cached: ${val}cm`);
        }
      }
    } catch (error) {
      console.error('[PkgCalc] Failed to fetch variant metafields:', error);
    }
  }

  function determinePackageSize(maxLength) {
    if (maxLength <= 119) {
      return { size: 'S', label: 'Small Package (up to 119cm)', maxLength };
    }
    if (maxLength <= 200) {
      return { size: 'M', label: 'Medium Package (120-200cm)', maxLength };
    }
    return { size: 'L', label: 'Large Package (201cm and above)', maxLength };
  }

  async function clearPackageAttributes() {
    if (lastCategory === null) return;
    lastCategory = null;
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: {
          'shipping_category': '',
          'package_size': '',
          'package_label': '',
          'max_length_cm': ''
        }})
      });
      log('Cleared package attributes (cart empty)');
    } catch (e) {
      console.error('[PkgCalc] Failed to clear cart attributes:', e);
    }
  }

  async function setPackageAttributes(packageInfo) {
    // Only CAT_S, CAT_M, CAT_L are allowed. Reject invalid or missing size.
    if (!packageInfo || !ALLOWED_SIZES.includes(packageInfo.size)) {
      log('Invalid or missing packageInfo.size — only S, M, L allowed; skipping attribute update');
      return;
    }
    // Puco/checkout use only shipping_category. Rest for personal use (reports, etc.).
    const shippingCategory = SHIPPING_CATEGORY_PREFIX + packageInfo.size;
    const newAttributes = {
      'shipping_category': shippingCategory,
      'package_size': packageInfo.size,
      'package_label': packageInfo.label,
      'max_length_cm': packageInfo.maxLength.toString()
    };

    const key = JSON.stringify(newAttributes);
    if (key === lastCategory) {
      log('Attributes unchanged, skipping update');
      return;
    }

    isUpdating = true;
    lastCategory = key;

    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: newAttributes })
      });
      if (res.ok) {
        log('Set package:', JSON.stringify(newAttributes));
      }
    } catch (e) {
      console.error('[PkgCalc] Failed to update cart attributes:', e);
    } finally {
      setTimeout(() => { isUpdating = false; }, 300);
    }
  }

  /**
   * Main calculation: always fetches fresh cart from /cart.js,
   * then looks up metafields from the cache for each item.
   */
  async function calculate() {
    if (DEBUG && DEBUG_OVERRIDE_CATEGORY.enabled) {
      log(`DEBUG OVERRIDE: forcing shipping_category = "${DEBUG_OVERRIDE_CATEGORY.value}"`);
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: {
            'shipping_category': DEBUG_OVERRIDE_CATEGORY.value,
            'package_size': 'DEBUG',
            'package_label': 'Debug override — set DEBUG_OVERRIDE_CATEGORY.enabled = false to resume normal operation',
            'max_length_cm': '0'
          }})
        });
        log('DEBUG OVERRIDE: cart attributes set');
      } catch (e) {
        console.error('[PkgCalc] DEBUG OVERRIDE failed:', e);
      }
      return;
    }

    if (isUpdating || isCalculating) return;
    isCalculating = true;

    try {
      // Always fetch fresh cart data (source of truth for what's in the cart)
      const res = await fetch('/cart.js');
      if (!res.ok) throw new Error('Cart fetch failed');
      const cart = await res.json();
      const items = cart.items;

      if (!items?.length) {
        await clearPackageAttributes();
        return;
      }

      // Find max length across all current cart items
      let maxLength = 0;
      let itemsWithLength = 0;
      for (const item of items) {
        const len = getVariantLength(item.variant_id, item.product_title, item.variant_title);
        if (len > 0) itemsWithLength++;
        if (len > maxLength) maxLength = len;
      }

      if (itemsWithLength === 0) {
        log('Warning: no items have custom.length metafield — defaulting to CAT_S');
      }

      log(`Max length: ${maxLength}cm (${itemsWithLength}/${items.length} items have length data)`);
      const packageInfo = determinePackageSize(maxLength);
      log(`Package: ${packageInfo.size} (${packageInfo.maxLength}cm)`);

      await setPackageAttributes(packageInfo);

    } catch (e) {
      console.error('[PkgCalc] Calculation error:', e);
    } finally {
      isCalculating = false;
    }
  }

  // ========== INITIALIZATION ==========

  function init() {
    log('Initialized');

    // Debounced recalculation
    let timeout;
    function recalc(delay = 1000) {
      clearTimeout(timeout);
      timeout = setTimeout(calculate, delay);
    }

    // Ingest any Liquid data already present on page load
    ingestLiquidMetafields();

    // Initial calculation
    recalc(1200);

    // PRIMARY TRIGGER: Liquid re-render fires cart:data:updated with fresh metafield data.
    // This is the most reliable source — it fires after cart-items section re-renders,
    // which includes the Liquid script that reads variant metafields server-side.
    document.addEventListener('cart:data:updated', () => {
      ingestLiquidMetafields();
      recalc(100);
    });

    // On cart:add, pre-fetch metafields via Storefront API (in case Liquid re-render is slow).
    // We do NOT trigger recalc here — we wait for cart:data:updated from the Liquid re-render.
    document.addEventListener('on:cart:add', async (e) => {
      const variantId = e.detail?.variantId;
      if (!variantId) return;
      await fetchAndCacheVariantMetafield(variantId);
    });

    // Intercept fetch for cart changes — catches /cart/change (quantity updates, removals).
    // We don't intercept /cart/add because the Liquid re-render (cart:data:updated) handles it.
    const origFetch = window.fetch;
    window.fetch = function(...args) {
      const p = origFetch.apply(this, args);
      const url = args[0];
      if (typeof url === 'string' && !isUpdating) {
        if (url.includes('/cart/change')) {
          p.then(() => recalc()).catch(() => {});
        }
      }
      return p;
    };

    // Quantity input changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name*="updates"]')) recalc(500);
    });

    // Remove button clicks
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-remove], .cart-remove, [data-cart-item-remove]') ||
          e.target.matches('[href*="/cart/change"]')) {
        recalc();
      }
    });

    // Observe the cart drawer for DOM changes to execute re-rendered cart data scripts
    // (innerHTML assignment doesn't execute <script> tags)
    const cartDrawer = document.getElementById('cart-drawer');
    if (cartDrawer) {
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type !== 'childList' || !mutation.addedNodes.length) continue;
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) return;
            if (node.matches?.('script[data-cart-data-script]')) {
              try { new Function(node.textContent)(); } catch (e) { console.error('[PkgCalc] Script exec error:', e); }
            }
            if (node.querySelectorAll) {
              node.querySelectorAll('script[data-cart-data-script]').forEach(script => {
                try { new Function(script.textContent)(); } catch (e) { console.error('[PkgCalc] Script exec error:', e); }
              });
            }
          });
        }
      });
      observer.observe(cartDrawer, { childList: true, subtree: true });
    }
  }

  // Start once
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
